import { Close } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  ButtonOwnProps,
  ClickAwayListener,
  Fade,
  IconButton,
  Link,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import React, { useEffect, useState } from "react";
import { isMobile, osName } from "react-device-detect";
import COLORS from "../constants/colors";

const isScrolledMoreThan10px = () => {
  if (typeof window !== "undefined") {
    return window.scrollY > 10;
  }
  return false;
};

interface QRButtonProps {
  variant?: ButtonOwnProps["variant"];
  color?: ButtonOwnProps["color"];
  label?: string;
  link?: boolean;
  linkColor?: string;
  sx?: any;
  endIcon?: React.ReactNode;
}

/**
 * Will s
 */
const QRButton = ({
  variant = "contained",
  color = "light",
  label = "Last ned appen",
  link = false,
  linkColor = "primary",
  sx,
  endIcon,
}: QRButtonProps) => {
  const [open, setOpen] = useState(false);
  const [selectedOS, setSelectedOS] = useState<"iOS" | "Android" | "Huawei">(
    "iOS"
  );
  const [OSLink, setOSLink] = useState<string>(
    "https://apps.apple.com/us/app/horde/id1471960734?ls=1"
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const onClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleScroll = () => {
    if (isScrolledMoreThan10px()) {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      if (osName === "iOS") {
        window.open(
          "https://apps.apple.com/us/app/horde/id1471960734?ls=1",
          "_blank"
        );
      } else if (osName === "Android") {
        window.open(
          "https://play.google.com/store/apps/details?id=com.horde",
          "_blank"
        );
      }
    } else {
      setAnchorEl(event.currentTarget);
      setOpen((previousOpen) => !previousOpen);
    }
  };

  const handleCloseClick = (event: MouseEvent | TouchEvent) => {
    // Prevent closing if clicking the button
    if (anchorEl && anchorEl.contains(event.target as Node)) {
      return;
    }
    onClose();
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "qr-popper" : undefined;

  return (
    <>
      {link ? (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid -- Link skal egentlig navigere
        <Link onClick={handleClick} color={linkColor} sx={sx}>
          <Box display="flex" alignItems="center" fontWeight="bold">
            {label}
            {endIcon && (
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ml: 1,
                }}
              >
                {endIcon}
              </Box>
            )}
          </Box>
        </Link>
      ) : (
        <Button
          variant={variant}
          color={color}
          onClick={handleClick}
          endIcon={endIcon}
          sx={sx}
        >
          {label}
        </Button>
      )}

      <Backdrop
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.7)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={open}
      >
        <ClickAwayListener onClickAway={handleCloseClick}>
          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            transition
            placement="top"
            sx={{ zIndex: 9999, position: "relative" }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={150}>
                <Stack
                  bgcolor={COLORS.WHITE[100]}
                  p="12px"
                  borderRadius="20px"
                  my="12px"
                  gap="6px"
                >
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap="6px"
                    justifyContent="space-between"
                  >
                    <Typography
                      color="primary"
                      variant="appDownloadPopperTitle"
                    >
                      Last ned Horde-appen
                    </Typography>
                    <Stack>
                      <IconButton onClick={onClose} size="small">
                        <Close />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Stack gap="6px">
                    <Stack
                      flexDirection="row"
                      justifyContent="space-evenly"
                      gap="6px"
                    >
                      <Button
                        color="dark"
                        variant={
                          selectedOS === "iOS" ? "contained" : "outlined"
                        }
                        onClick={() => {
                          setSelectedOS("iOS");
                          setOSLink(
                            "https://apps.apple.com/us/app/horde/id1471960734?ls=1"
                          );
                        }}
                        size="small"
                      >
                        iPhone
                      </Button>
                      <Button
                        variant={
                          selectedOS === "Android" ? "contained" : "outlined"
                        }
                        onClick={() => {
                          setSelectedOS("Android");
                          setOSLink(
                            "https://play.google.com/store/apps/details?id=com.horde"
                          );
                        }}
                        color="dark"
                      >
                        Android
                      </Button>
                      <Button
                        color="dark"
                        variant={
                          selectedOS === "Huawei" ? "contained" : "outlined"
                        }
                        onClick={() => {
                          setSelectedOS("Huawei");
                          setOSLink(
                            "https://appgallery.huawei.com/#/app/C102218503"
                          );
                        }}
                      >
                        Huawei
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack alignItems="center">
                    <QRCodeSVG
                      value={OSLink}
                      size={340}
                      bgColor={COLORS.WHITE[100]}
                      fgColor={COLORS.DARK_BLUE[100]}
                    />
                  </Stack>
                </Stack>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      </Backdrop>
    </>
  );
};
export default QRButton;
